import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type {
  InventoryItem,
  InventoryMovement,
  ProcedureCode,
  ProcedureMaterial,
} from "@dentacore/shared-types";

import { listProcedureCodes } from "../api/catalogue";
import { listCatalogue } from "../api/suppliers";
import {
  addProcedureMaterial,
  adjustInventory,
  createInventoryItem,
  editProcedureMaterial,
  listInventory,
  listInventoryMovements,
  listProcedureMaterials,
  openInventoryUnit,
  removeProcedureMaterial,
  setWantToBuy,
  updateInventoryItem,
} from "../api/inventory";
import { useActiveBranch } from "../api/use-active-branch";
import { Toast } from "../settings/toast";
import { useWriter } from "../settings/use-writer";
import type { Follow } from "../ui/paging";
import { HelpTip } from "../ui/popovers";
import { AddItemDialog } from "./add-item-dialog";
import { ItemPanel } from "./item-panel";
import type { ItemAsk, ItemAskRequest, ItemTab } from "./item-panel";
import { ShelfList } from "./shelf-list";
import { atLocation } from "../api/client";
import {
  Badges,
  CopySwitch,
  LocationField,
  across,
  groupByName,
  useShown,
} from "../shell/locations";
import type { Located } from "../shell/locations";
import type { ShelfRow } from "./shelf-list";
import { isLow, packSplit, plural, usesText } from "./stock";
import { StockColumns, StockRow } from "./stock-row";
import { TrayPanel } from "./tray-panel";
import styles from "./inventory.module.css";
import cat from "../supply/catalogue.module.css";
import { StatusToggles, toggled } from "../supply/status-toggles";
import { useCan } from "../auth/use-can";
import { useModules } from "../shell/modules-context";
import { useBranchCurrency } from "../api/use-branch-currency";
import { useCart } from "../supply/cart";
import { announceRing } from "../shell/quick-ring";
import { RING_ICONS } from "../shell/ring/ring-icons";
import { useRingProvider } from "../shell/ring/ring-registry";

type Tab = "stock" | "trays";

/**
 * **What the shelf can be narrowed to, and how it is ordered** (Emile,
 * 2026-10-17: *"add a way to filter items by price, status (low, etc)"*). The
 * same toggles as the catalogue, several on at once; none on is everything.
 */
type ShelfStatus = "low" | "ordered" | "want" | "stock";
const SHELF_STATUSES: readonly { value: ShelfStatus; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "ordered", label: "On order" },
  { value: "want", label: "Want to buy" },
  { value: "stock", label: "In stock" },
];
const hasShelfStatus = (item: InventoryItem, status: ShelfStatus): boolean =>
  status === "low"
    ? isLow(item)
    : status === "ordered"
      ? item.onOrder
      : status === "want"
        ? item.wantToBuy
        : !isLow(item);

type ShelfSort = "low" | "name" | "cheapest";
const SHELF_SORTS: readonly { value: ShelfSort; label: string }[] = [
  { value: "low", label: "Low first" },
  { value: "name", label: "Name A–Z" },
  // No dearest: Emile took it off the catalogue's sort, and the two agree.
  { value: "cheapest", label: "Cheapest pack" },
];

/**
 * Inventory — **the item's figures in columns, and a click opens the item's
 * panel** (IC1–IC8, 2026-10-06).
 *
 * Anthony: *"Add everything in columns outside. Clicking on an item should only
 * show u: Add - remove - Set low threshold."* So a row is the item, what is
 * left in its open pack, how many packs are still sealed, and its price per
 * pack (IC1). The click first opened a small card (IC3) with the rest on a ⋯
 * per row (IC4); that took three clicks to reach History, so the panel came
 * back the same day (IC6): its first tab, **Stock**, is the three actions
 * alone, then History and Suppliers. Low items still come first with a warm
 * dot (IV2). **Nothing the page did before was removed** (CS1).
 *
 * ── Why it is a destination at all ─────────────────────────────────────────
 *
 * It was the last section of a location's settings page, and Settings is
 * admin-only — while TP17 deliberately widened adjusting a count to *"the person
 * at the chair"*. So this route carries no admin gate; the per-action gate below
 * does the work, and the server refuses a non-admin's write regardless.
 *
 * ── One location at a time ─────────────────────────────────────────────────
 *
 * Stock is physical and sits in a room, so it is per branch (TP2) and the API
 * scopes it by the active-branch header. The page follows the branch switcher,
 * and drops whatever it had open when the branch changes — each location owns
 * its own stock and its own catalogue (ADR-0008).
 */
export function InventoryPage(): React.JSX.Element {
  const branchId = useActiveBranch();
  const can = useCan();
  // Setting stock up (TP17) and editing a tray are separate permissions now
  // (STORY-166): a tray is a procedure's bill of materials.
  const isAdmin = can("inventory.items.manage");
  // Ordering and receiving are the chairside half, like adjusting a count (SU-D8).
  const canOrder = can("inventory.movement.record");
  const currency = useBranchCurrency();
  // `?item=<id>` opens that item — what a low-stock notice links to (STORY-171).
  const [params, setParams] = useSearchParams();
  const canEditTrays = can("procedures.manage");
  // Suppliers and Orders moved to their own Supply screen (STORY-189a, MOD-D21).
  const hasSupply = useModules().has("SUPPLY");
  const { busy, toast, dismiss, fail, run } = useWriter();
  // The locations ticked in the top bar: several show as one shelf.
  const places = useShown();
  const [addAt, setAddAt] = useState<string | null>(null);

  const [tab, setTab] = useState<Tab>("stock");
  const [items, setItems] = useState<readonly InventoryItem[] | null>(null);
  const [codes, setCodes] = useState<readonly ProcedureCode[] | null>(null);
  const [query, setQuery] = useState("");
  const [statuses, setStatuses] = useState<ReadonlySet<ShelfStatus>>(new Set());
  const [sort, setSort] = useState<ShelfSort>("low");
  const [retiredView, setRetiredView] = useState(false);
  const [adding, setAdding] = useState(false);

  /** The item open in the panel beside the list (IC6). */
  const [itemId, setItemId] = useState<string | null>(null);
  const [itemTab, setItemTab] = useState<ItemTab>("stock");
  const [movements, setMovements] = useState<readonly InventoryMovement[] | null>(null);
  const [follow, setFollow] = useState<Follow | null>(null);

  const [codeId, setCodeId] = useState<string | null>(null);
  const [materials, setMaterials] = useState<readonly ProcedureMaterial[] | null>(null);
  const [editingTray, setEditingTray] = useState(false);

  /**
   * A form the quick ring asked the open item's panel to start (STORY-221) —
   * held against the item it is for, so it never lands on another one.
   */
  const [ask, setAsk] = useState<(ItemAskRequest & { itemId: string }) | null>(null);
  const askSeq = useRef(0);
  const clearAsk = useCallback(() => setAsk(null), []);
  const navigate = useNavigate();
  const cart = useCart();

  /** Retired items included, so *Retired items* has something to show. */
  const reloadItems = useCallback(
    async (signal?: AbortSignal) => {
      setItems(
        places.many
          ? await across(places.ids, () => listInventory(true, signal))
          : await listInventory(true, signal),
      );
    },
    [places.key],
  );

  const reloadMovements = useCallback(async (id: string) => {
    setMovements(await listInventoryMovements(id));
  }, []);

  useEffect(() => {
    if (branchId === null) return;

    const controller = new AbortController();
    setItems(null);
    setCodes(null);
    setItemId(null);
    setCodeId(null);
    setEditingTray(false);

    reloadItems(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setItems([]);
      fail(cause instanceof Error ? cause.message : "Stock could not be loaded.");
    });

    /**
     * **Active procedures only.** A retired procedure is never booked again, so
     * its tray answers a question nobody can ask.
     */
    listProcedureCodes(false, controller.signal)
      .then(setCodes)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setCodes([]);
        fail(cause instanceof Error ? cause.message : "The procedures could not be read.");
      });

    return () => controller.abort();
  }, [branchId, reloadItems, fail]);

  // The open item's history, read as it opens: the History tab shows its count.
  useEffect(() => {
    setMovements(null);
    if (itemId === null) return;

    const controller = new AbortController();
    listInventoryMovements(itemId, controller.signal)
      .then(setMovements)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setMovements([]);
        fail(cause instanceof Error ? cause.message : "That history could not be read.");
      });

    return () => controller.abort();
  }, [itemId, fail]);

  useEffect(() => {
    setMaterials(null);
    if (codeId === null) return;

    const controller = new AbortController();
    listProcedureMaterials(codeId, controller.signal)
      .then(setMaterials)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setMaterials([]);
        fail(cause instanceof Error ? cause.message : "That tray could not be read.");
      });

    return () => controller.abort();
  }, [codeId, fail]);

  const active = useMemo(() => (items ?? []).filter((item) => item.isActive), [items]);
  const retiredCount = (items ?? []).length - active.length;

  /**
   * **One row per item across the locations shown**: copies that share
   * a name are drawn once, their counts added up, the first copy's id and price kept.
   */
  const mergeCopies = (list: readonly InventoryItem[]): InventoryItem[] => {
    if (!places.many) return [...list];
    return groupByName(list as Located<InventoryItem>[], (row) => row.name).map(
      ({ first, copies }) =>
        copies.length === 1
          ? first
          : {
              ...first,
              quantityOnHand: copies.reduce((sum, copy) => sum + copy.quantityOnHand, 0),
              bookedQuantity: copies.reduce((sum, copy) => sum + copy.bookedQuantity, 0),
              lowAt: copies.reduce((sum, copy) => sum + copy.lowAt, 0),
              onOrder: copies.some((copy) => copy.onOrder),
              wantToBuy: copies.some((copy) => copy.wantToBuy),
            },
    );
  };

  /**
   * What the stock list shows. **Low items first** (IV2), then by name; the
   * retired view by name alone. **Search matches the name only** — a clinician
   * looking for a shade types the shade, and matching the pack as well would
   * surface every boxed item for the word "box".
   */
  const shown = useMemo(() => {
    if (retiredView) {
      return (items ?? [])
        .filter((item) => !item.isActive)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const needle = query.trim().toLocaleLowerCase();
    const order: Record<ShelfSort, (a: InventoryItem, b: InventoryItem) => number> = {
      low: (a, b) => Number(isLow(b)) - Number(isLow(a)) || a.name.localeCompare(b.name),
      name: (a, b) => a.name.localeCompare(b.name),
      cheapest: (a, b) => a.priceCents - b.priceCents || a.name.localeCompare(b.name),
    };
    return mergeCopies(active)
      .filter(
        (item) =>
          (needle === "" || item.name.toLocaleLowerCase().includes(needle)) &&
          (statuses.size === 0 || [...statuses].some((s) => hasShelfStatus(item, s))),
      )
      .sort(order[sort]);
  }, [items, active, query, retiredView, statuses, sort, places.many]);

  /** Each row's copies at the locations shown, by the id of any one of them. */
  const copiesOf = useMemo(() => {
    const map = new Map<string, Located<InventoryItem>[]>();
    if (!places.many) return map;
    for (const group of groupByName(active as Located<InventoryItem>[], (row) => row.name)) {
      for (const copy of group.copies) map.set(copy.id, group.copies);
    }
    return map;
  }, [active, places.many]);

  /** How many items each status would show, for the toggles' counts. */
  const statusOptions = SHELF_STATUSES.map(({ value, label }) => ({
    value,
    label,
    count: active.filter((item) => hasShelfStatus(item, value)).length,
  }));

  const openItem = (id: string | null): void => {
    setItemId(id);
    setItemTab("stock");
    setAsk(null);
  };

  /**
   * `?item=<id>` — a low-stock notice's link (STORY-171; it was a bare id, which
   * routed to "page not found"). Opens the item on its Suppliers tab once the
   * list has loaded, then drops the parameter so a reload does not reopen it.
   */
  const linked = params.get("item");
  useEffect(() => {
    if (linked === null || items === null) return;
    const found = items.find((row) => row.id === linked);
    if (found !== undefined) {
      setTab("stock");
      setRetiredView(!found.isActive);
      setQuery("");
      setItemId(found.id);
      setItemTab("stock");
      setFollow({ key: found.id });
    }
    setParams({}, { replace: true });
  }, [linked, items, setParams]);

  const item = itemId === null ? null : ((items ?? []).find((row) => row.id === itemId) ?? null);
  const code = codeId === null ? null : ((codes ?? []).find((row) => row.id === codeId) ?? null);

  /** After anything that moves a count: the list, and the open item's history. */
  const reloadAfterMove = async (id: string): Promise<void> => {
    await reloadItems();
    await reloadMovements(id);
  };

  /** *Opened one* (TP7) — the panel's ⋯ entry and the quick ring's, one write. */
  const openUnit = (target: InventoryItem): void => {
    void run(
      () => openInventoryUnit(target.id),
      () => reloadAfterMove(target.id),
      `One ${target.name} opened.`,
    );
  };

  // ── The quick ring (STORY-221, QR7–QR9) ─────────────────────────────────
  // Every action below is one this screen already has: it opens the same panel
  // form, the same dialog or the same tab its own button does, or runs the same
  // write. All stock is Supply (MS-D2), so every one carries `module: "SUPPLY"`.

  /** Open an item's panel with one of its forms started, as its button would. */
  const askItem = (id: string, what: ItemAsk): void => {
    openItem(id);
    askSeq.current += 1;
    setAsk({ itemId: id, what, seq: askSeq.current });
  };

  /**
   * *Add to cart* — the catalogue's own `cart.add` (SU-D17), with the line its
   * primary button adds: the cheapest offer for this item, one pack, no reason.
   * Read from this item's own location, as the catalogue would be.
   */
  const addToCart = async (target: InventoryItem): Promise<void> => {
    const at = places.many ? ((target as Partial<Located<InventoryItem>>).at ?? null) : null;
    try {
      const rows = await atLocation(at, () => listCatalogue());
      const row = rows.find((entry) => entry.inStock?.itemId === target.id);
      const offer =
        row?.offers.find((candidate) => row.cheapestIds.includes(candidate.id)) ?? row?.offers[0];
      if (row === undefined || offer === undefined || row.inStock === null) {
        announceRing(`No supplier sells ${target.name} yet.`);
        return;
      }
      cart.add({
        inventoryItemId: row.inStock.itemId,
        itemName: row.itemName,
        supplierPriceId: offer.id,
        supplierId: offer.supplierId,
        supplierName: offer.supplierName,
        pack: offer.pack,
        usesPerPack: offer.usesPerPack,
        priceCents: offer.priceCents,
        cheapest: row.cheapestIds.includes(offer.id),
        packs: "1",
        reason: "",
      });
      announceRing(`${target.name} is in the cart, from ${offer.supplierName}.`);
    } catch (cause: unknown) {
      fail(cause instanceof Error ? cause.message : "The catalogue could not be read.");
    }
  };

  useRingProvider("inv.item", (element) => {
    const id = element.dataset.item;
    // The record the panel would act on, and the row's figures (merged across
    // the locations shown) for the hub's second line.
    const target = (items ?? []).find((row) => row.id === id);
    if (id === undefined || target === undefined) return null;
    const figures = shown.find((row) => row.id === id) ?? target;
    const split = packSplit(figures);
    const meta = !target.isActive
      ? "Retired"
      : [
          split.sealed === null ? split.open : `${String(split.sealed)} unopened`,
          split.sealed !== null && split.open !== null ? `${split.open} open` : null,
          isLow(figures) ? "Running low" : null,
        ]
          .filter((part): part is string => part !== null)
          .join(" · ");
    const live = target.isActive;
    return {
      ring: "inv.item",
      title: target.name,
      meta,
      subject: { type: "item", id: target.id },
      actions: [
        live && {
          key: "remove",
          label: "Remove",
          icon: RING_ICONS.minus,
          hint: "Takes some off the shelf",
          needs: ["inventory.movement.record"],
          module: "SUPPLY",
          run: () => askItem(target.id, "remove"),
        },
        live && {
          key: "add",
          label: "Add",
          icon: RING_ICONS.plus,
          hint: "Puts some on the shelf",
          needs: ["inventory.movement.record"],
          module: "SUPPLY",
          run: () => askItem(target.id, "add"),
        },
        // A bulk item moves only when one is opened (TP7) — the ⋯'s own rule.
        live &&
          target.kind === "BULK" && {
            key: "opened",
            label: "Opened one",
            icon: RING_ICONS.open,
            hint: "A new pack was opened",
            needs: ["inventory.movement.record"],
            module: "SUPPLY",
            run: () => {
              openItem(target.id);
              openUnit(target);
            },
          },
        // On order already: no Add to cart unless marked to buy (MOD-D24, WB-D5).
        live &&
          (!target.onOrder || target.wantToBuy) &&
          !cart.has(target.id) && {
            key: "order",
            label: "Add to cart",
            icon: RING_ICONS.cart,
            hint: "Orders it with the next batch",
            needs: ["inventory.movement.record"],
            module: "SUPPLY",
            run: () => {
              void addToCart(target);
            },
          },
        live &&
          target.kind !== "REUSABLE" && {
            key: "low",
            label: "Low threshold",
            icon: RING_ICONS.gauge,
            hint: "When it counts as low",
            needs: ["inventory.items.manage"],
            module: "SUPPLY",
            run: () => askItem(target.id, "low"),
          },
        live && {
          key: "price",
          label: "Change price",
          icon: RING_ICONS.tag,
          hint: "Price per pack",
          needs: ["inventory.items.manage"],
          module: "SUPPLY",
          run: () => askItem(target.id, "price"),
        },
      ],
    };
  });

  useRingProvider("page", () => ({
    ring: "page.inventory",
    title: "Inventory",
    meta: `${String(mergeCopies(active).filter(isLow).length)} running low`,
    actions: [
      items !== null && {
        key: "add",
        label: "Add an item",
        icon: RING_ICONS.plus,
        hint: "A new item on the shelf",
        needs: ["inventory.items.manage"],
        module: "SUPPLY",
        run: () => setAdding(true),
      },
      {
        key: "cart",
        label: "Open the cart",
        icon: RING_ICONS.cart,
        hint: "What you are about to order",
        // The sidebar's Cart link asks the same (nav-items.ts).
        needs: ["inventory.view"],
        module: "SUPPLY",
        run: () => {
          void navigate("/orders/cart");
        },
      },
      tab === "stock"
        ? {
            key: "trays",
            label: "Procedure trays",
            icon: RING_ICONS.list,
            hint: "What each procedure uses",
            needs: ["inventory.view"],
            module: "SUPPLY",
            run: () => setTab("trays"),
          }
        : {
            key: "stock",
            label: "Stock",
            icon: RING_ICONS.box,
            hint: "What is on the shelf",
            needs: ["inventory.view"],
            module: "SUPPLY",
            run: () => setTab("stock"),
          },
    ],
  }));

  const stockRows: ShelfRow[] = shown.map((row) => ({
    key: row.id,
    node: (
      <StockRow
        item={row}
        currency={currency}
        open={row.id === itemId}
        onOpen={(open) => openItem(open ? row.id : null)}
        badges={
          places.many ? (
            <Badges ats={(copiesOf.get(row.id) ?? []).map((copy) => copy.at)} shown={places} />
          ) : undefined
        }
      />
    ),
  }));

  const trayRows: ShelfRow[] = (codes ?? []).map((row) => {
    const on = row.id === codeId;
    return {
      key: row.id,
      node: (
        <button
          type="button"
          className={on ? `${styles.row} ${styles.rowOn}` : styles.row}
          aria-current={on ? "true" : undefined}
          onClick={() => {
            setCodeId(on ? null : row.id);
            setEditingTray(false);
          }}
          data-card=""
          data-testid={`proc-materials-${row.id}`}
        >
          <span className={styles.rowName}>
            <span>{row.label}</span>
          </span>
        </button>
      ),
    };
  });

  /**
   * One write to a tray. The write returns the new tray, so the open panel is
   * refreshed from the response; **the item list is re-read too**, because
   * `usedByProcedureCount` moves with every add and remove.
   */
  const writeMaterials = (
    write: () => Promise<readonly ProcedureMaterial[]>,
    done: string,
  ): Promise<boolean> =>
    run(
      async () => {
        setMaterials(await write());
      },
      () => reloadItems(),
      done,
    );

  // One count per item across the locations shown, not one per copy.
  const shelf = mergeCopies(active);
  const lowCount = shelf.filter(isLow).length;

  return (
    <div className={styles.page} data-testid="inventory-page">
      <header className={styles.head}>
        <h1 className={styles.title}>Inventory</h1>
        <HelpTip label="What a count means" testId="inventory-help">
          What this location keeps on the shelf. Counts are in uses — a box of 20 cut into ten is
          200 of them.
        </HelpTip>
        <div className={styles.headActions}>
          {/* Visible to everyone, as *Show retired* was (IV8). */}
          {tab === "stock" && retiredCount > 0 && (
            <button
              type="button"
              className={styles.quiet}
              aria-pressed={retiredView}
              onClick={() => {
                setRetiredView((was) => !was);
                openItem(null);
              }}
              data-testid="inventory-toggle-retired"
            >
              {retiredView ? "Back to the shelf" : "Retired items"}
            </button>
          )}
          {isAdmin && items !== null && (
            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={() => setAdding(true)}
              data-testid="inventory-add-open"
            >
              Add an item
            </button>
          )}
        </div>
      </header>

      <div className={styles.tabs} role="tablist" aria-label="Stock or procedure trays">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "stock"}
          className={tab === "stock" ? `${styles.tab} ${styles.tabOn}` : styles.tab}
          onClick={() => setTab("stock")}
          data-testid="inventory-stock-tab"
        >
          Stock
          <span className={styles.tabCount}>{shelf.length}</span>
          {lowCount > 0 && (
            <span className={styles.tabWarm} data-testid="inventory-low-count">
              {lowCount} low
            </span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "trays"}
          className={tab === "trays" ? `${styles.tab} ${styles.tabOn}` : styles.tab}
          onClick={() => setTab("trays")}
          data-testid="inventory-trays"
        >
          Procedure trays
          <span className={styles.tabCount}>{(codes ?? []).length}</span>
        </button>
      </div>

      {tab === "stock" ? (
        <div className={styles.body}>
          <ShelfList
            testId="inventory-list"
            head={
              retiredView ? (
                <>
                  <span className={styles.headLabel}>Retired items</span>
                  <span className={styles.headCount}>{shown.length}</span>
                </>
              ) : (
                <>
                  <input
                    type="search"
                    className={styles.search}
                    placeholder="Search the shelf…"
                    aria-label="Search the shelf"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    data-testid="inventory-search"
                  />
                  <StatusToggles
                    options={statusOptions}
                    on={statuses}
                    onToggle={(value) => setStatuses((was) => toggled(was, value as ShelfStatus))}
                    onClear={() => setStatuses(new Set())}
                    testId="inventory-status"
                  />
                  <select
                    className={`${cat.select} ${cat.selectSmall}`}
                    value={sort}
                    aria-label="Sort by"
                    data-testid="inventory-sort"
                    onChange={(event) => setSort(event.target.value as ShelfSort)}
                  >
                    {SHELF_SORTS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {(query.trim() !== "" || statuses.size > 0) && (
                    <span className={styles.headCount} data-testid="inventory-search-count">
                      {plural(shown.length, "item")}
                    </span>
                  )}
                </>
              )
            }
            columns={<StockColumns />}
            rows={stockRows}
            empty={
              items === null
                ? "Loading…"
                : (query.trim() !== "" || statuses.size > 0) && !retiredView
                  ? "Nothing on the shelf matches."
                  : isAdmin
                    ? "Nothing here yet. Add what this location keeps on the shelf."
                    : "This location has not listed its stock yet."
            }
            noun="items"
            follow={follow}
            resetOn={`${String(branchId)}|${query}|${String(retiredView)}|${[...statuses].join()}|${sort}`}
          />

          {item !== null && (
            <ItemPanel
              key={item.id}
              item={item}
              movements={movements}
              canMove={canOrder}
              canSetUp={isAdmin}
              busy={busy}
              tab={itemTab}
              onTab={setItemTab}
              onClose={() => openItem(null)}
              supplyHref={hasSupply ? `/supply` : null}
              locations={
                places.many ? (
                  <CopySwitch
                    copies={copiesOf.get(item.id) ?? []}
                    current={item.id}
                    shown={places}
                    onPick={(id) => setItemId(id)}
                  />
                ) : undefined
              }
              onMove={(delta) =>
                // Add and Remove send a number and nothing else (IC5). The list
                // follows the item, which may re-sort out of "low" — or into it.
                run(
                  () => adjustInventory(item.id, { quantityDelta: delta }),
                  async () => {
                    await reloadAfterMove(item.id);
                    setFollow({ key: item.id });
                  },
                  delta > 0 ? `${usesText(delta)} added.` : `${usesText(-delta)} taken off.`,
                )
              }
              onLowAt={(lowAt) =>
                run(
                  () => updateInventoryItem(item.id, { lowAt }),
                  async () => {
                    await reloadItems();
                    setFollow({ key: item.id });
                  },
                  `${item.name} alerts at ${usesText(lowAt)} or fewer.`,
                )
              }
              onWantToBuy={(want) => {
                void run(
                  () => setWantToBuy(item.id, want),
                  () => reloadItems(),
                  want
                    ? `${item.name} is starred at the top of the catalogue.`
                    : `${item.name} is no longer marked to buy.`,
                );
              }}
              onOpenUnit={() => openUnit(item)}
              ask={ask !== null && ask.itemId === item.id ? ask : null}
              onAsked={clearAsk}
              onRetire={() => {
                // Gone from the working list, because that is what retiring is
                // for — so the panel goes with it.
                void run(
                  () => updateInventoryItem(item.id, { isActive: false }),
                  () => reloadItems(),
                  `${item.name} retired.`,
                ).then((landed) => {
                  if (landed) openItem(null);
                });
              }}
              onPrice={(priceCents) =>
                run(
                  () => updateInventoryItem(item.id, { priceCents }),
                  () => reloadItems(),
                  `${item.name}'s price is saved.`,
                )
              }
              onEstimate={(estimatedUses) =>
                run(
                  () => updateInventoryItem(item.id, { estimatedUses }),
                  () => reloadItems(),
                  `${item.name}'s estimate is saved.`,
                )
              }
              onEdit={(patch) =>
                run(
                  () => updateInventoryItem(item.id, patch),
                  () => reloadItems(),
                  `${patch.name ?? item.name} is saved.`,
                )
              }
              onRestore={() => {
                // Back on the shelf, and the list goes with it to where it now is.
                void run(
                  () => updateInventoryItem(item.id, { isActive: true }),
                  () => reloadItems(),
                  `${item.name} is back on the list.`,
                ).then((landed) => {
                  if (!landed) return;
                  setRetiredView(false);
                  setFollow({ key: item.id });
                });
              }}
            />
          )}
        </div>
      ) : (
        <div className={styles.body}>
          <ShelfList
            testId="inventory-trays-list"
            head={
              <>
                <span className={styles.headLabel}>Procedures</span>
                <span className={styles.headCount}>{(codes ?? []).length}</span>
                <HelpTip label="What a tray is for" testId="inventory-trays-help">
                  What each procedure takes off the shelf. Written once here, it is what the prep
                  list on a booking shows and what completing that visit deducts.
                </HelpTip>
              </>
            }
            rows={trayRows}
            empty={codes === null ? "Loading…" : "This location has no procedures yet."}
            noun="procedures"
            resetOn={branchId}
          />

          {code !== null && (
            <TrayPanel
              key={code.id}
              code={code}
              materials={materials}
              items={items}
              isAdmin={canEditTrays}
              busy={busy}
              editing={editingTray}
              onEditing={setEditingTray}
              onClose={() => {
                setCodeId(null);
                setEditingTray(false);
              }}
              onAdd={(body) =>
                writeMaterials(() => addProcedureMaterial(code.id, body), "Added to the tray.")
              }
              onEdit={(materialId, body) => {
                void writeMaterials(
                  () => editProcedureMaterial(code.id, materialId, body),
                  "How much it needs is updated.",
                );
              }}
              onRemove={(materialId, name) => {
                void writeMaterials(
                  () => removeProcedureMaterial(code.id, materialId),
                  `${name} taken off this procedure.`,
                );
              }}
            />
          )}
        </div>
      )}

      {adding && (
        <AddItemDialog
          location={<LocationField shown={places} value={addAt} onChange={setAddAt} />}
          busy={busy}
          onClose={() => setAdding(false)}
          onCreate={async (body) => {
            // The new item opens beside the list, on whichever page it landed.
            const made: { item: InventoryItem | null } = { item: null };
            const landed = await run(
              async () => {
                made.item = await atLocation(places.many ? (addAt ?? places.active) : null, () =>
                  createInventoryItem(body),
                );
              },
              () => reloadItems(),
              "Item added.",
            );
            if (landed && made.item !== null) {
              // The new item opens beside the list, on whichever page it landed.
              setRetiredView(false);
              setQuery("");
              openItem(made.item.id);
              setFollow({ key: made.item.id });
            }
            return landed;
          }}
        />
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </div>
  );
}
